import { Component, input, output } from '@angular/core';
import {
  ADMIN_MENU_ICON_BY_ROUTE,
  AdminIconComponent,
  AdminSidebarGroupComponent,
  type AdminMenuChild,
  AdminSidebarItemComponent,
  type AdminMenuIconName,
} from '../../shared/admin';

export type AdminMenuLink = {
  label: string;
  route: string;
  roles?: string[];
};

export type AdminMenuGroup = {
  label: string;
  icon: AdminMenuIconName;
  children: AdminMenuChild[];
  defaultExpanded?: boolean;
  roles?: string[];
};

export type AdminMenuSection = {
  section: string;
};

export type AdminMenuItem = AdminMenuLink | AdminMenuGroup | AdminMenuSection;

export function isAdminMenuGroup(item: AdminMenuItem): item is AdminMenuGroup {
  return 'children' in item;
}

export function isAdminMenuSection(item: AdminMenuItem): item is AdminMenuSection {
  return 'section' in item;
}

@Component({
  selector: 'app-admin-sidebar',
  standalone: true,
  imports: [AdminSidebarItemComponent, AdminSidebarGroupComponent, AdminIconComponent],
  template: `
    @if (mobileOpen()) {
      <div class="cg-admin-sidebar-backdrop" (click)="mobileOpenChange.emit(false)" aria-hidden="true"></div>
    }
    <aside
      class="cg-admin-sidebar"
      [class.is-collapsed]="collapsed()"
      [class.is-open]="mobileOpen()"
    >
      <div class="cg-admin-sidebar__brand">
        <div class="cg-admin-sidebar__brand-main">
          <span class="cg-admin-sidebar__logo" aria-hidden="true">
            <img
              class="cg-admin-sidebar__logo-img"
              src="/assets/images/logo.png"
              alt=""
            />
          </span>
          <span class="cg-admin-sidebar__brand-label">Admin</span>
        </div>
        <button
          type="button"
          class="cg-admin-sidebar__collapse-btn"
          [attr.aria-label]="collapsed() ? 'Expand sidebar' : 'Collapse sidebar'"
          (click)="collapseToggle.emit()"
        >
          <app-admin-icon [name]="collapsed() ? 'chevrons-right' : 'chevrons-left'" [size]="18" />
        </button>
      </div>

      <nav class="cg-admin-sidebar__nav" aria-label="Platform navigation">
        @for (item of menu(); track trackMenuItem(item)) {
          @if (menuSection(item)) {
            <div class="cg-admin-sidebar__section" role="presentation">{{ item.section }}</div>
          } @else if (menuGroup(item)) {
            <app-admin-sidebar-group
              [label]="item.label"
              [icon]="item.icon"
              [children]="item.children"
              [defaultExpanded]="item.defaultExpanded ?? true"
              [collapsed]="collapsed()"
              (navSelected)="navSelected.emit()"
            />
          } @else {
            <app-admin-sidebar-item
              [label]="item.label"
              [route]="item.route"
              [icon]="iconFor(item.route)"
              (click)="navSelected.emit()"
            />
          }
        }
      </nav>

      <footer class="cg-admin-sidebar__footer">
        <span class="cg-admin-sidebar__footer-text">© Career Grad Platform</span>
      </footer>
    </aside>
  `,
})
export class AdminSidebarComponent {
  readonly menu = input.required<AdminMenuItem[]>();
  readonly collapsed = input(false);
  readonly mobileOpen = input(false);
  readonly mobileOpenChange = output<boolean>();
  readonly navSelected = output<void>();
  readonly collapseToggle = output<void>();

  iconFor(route: string): AdminMenuIconName {
    return ADMIN_MENU_ICON_BY_ROUTE[route] ?? 'layout-dashboard';
  }

  trackMenuItem(item: AdminMenuItem): string {
    if (isAdminMenuSection(item)) return `section:${item.section}`;
    if (isAdminMenuGroup(item)) return `group:${item.label}`;
    return item.route;
  }

  menuGroup(item: AdminMenuItem): item is AdminMenuGroup {
    return isAdminMenuGroup(item);
  }

  menuSection(item: AdminMenuItem): item is AdminMenuSection {
    return isAdminMenuSection(item);
  }
}
